import React, { useState, useMemo, useEffect, useCallback, useRef, type ReactNode } from "react";
import {
  ALL_KEYS,
  MODULES,
  cmd,
  load,
  loadModule,
  view,
  invoiceEdit,
  billEdit,
  journalEdit,
  invoiceDetail,
  billDetail,
  journalLines,
  today,
  dbId,
  type Data,
  type Row,
  type Val,
  type ModuleDef,
  type ModuleTable,
} from "./Api/Accountingapi";
import styles from "./Accounting.module.css";

import {
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  MenuButton,
  Tab,
  TabList,
  List,
  ListItem,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  SearchBox,
  Select,
} from "@fluentui/react-components";
import {
  ChevronDown16Regular,
  Settings16Regular,
  Timer16Regular,
  Pin16Regular,
  ArrowDownloadRegular,
  DocumentArrowDownRegular,
  DocumentFlowchartRegular,
  LinkRegular,
  DocumentDataLinkRegular,
  AppTitleRegular,
  NoteEditRegular,
  TopSpeedRegular,
  PersonAccountsRegular,
  ContactCardLinkRegular,
  SlideTextCallRegular,
  NoteAddRegular,
  FolderPeopleRegular,
  CalendarMonthRegular,
  MoneyRegular,
  ReceiptRegular,
  WalletCreditCardRegular,
  DocumentTableRegular,
  ChartMultipleRegular,
  PeopleRegular,
  BuildingRegular,
  BuildingBankRegular,
  ArrowSyncCircleRegular,
  TextBulletListLtrRegular,
  BookRegular,
  BookOpenRegular,
  ScalesRegular,
  BoxRegular,
  HistoryRegular,
  ReceiptMoneyRegular,
  DocumentBulletListRegular,
  Add20Regular,
  Edit20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Dismiss24Regular,
  Checkmark20Regular,
  ArrowDownload20Regular,
  ArrowUndo20Regular,
  ChevronUp12Regular,
  ChevronDown12Regular,
  Send20Regular,
  Payment20Regular,
  Prohibited20Regular,
} from "@fluentui/react-icons";
import { Calendar } from "@fluentui/react-calendar-compat";
import AccountingLogo from "../../assets/images/Accounting.svg"; // adjust path if needed

/* ---------- small UI helpers ---------- */
function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`${styles.card} ${className}`}>{children}</section>;
}

function MiniIcon({ type }: { type: "expense" | "income" | "bill" }) {
  const glyphs = { expense: "↗", income: "+", bill: "$" };
  return (
    <div className={`${styles["mini-icon"]} ${styles[type]}`}>
      <span>{glyphs[type]}</span>
    </div>
  );
}

function Bars({ values, max = 100 }: { values: number[]; max?: number }) {
  return (
    <div className={styles["vertical-bars"]}>
      {values.map((v, i) => (
        <div className={styles["bar-slot"]} key={i}>
          <span style={{ height: `${(v / max) * 100}%` }} />
        </div>
      ))}
    </div>
  );
}

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/* =====================================================================================================
   Accounting modules (everything except the dashboard). All data comes from the API; nothing is stored here.
   ===================================================================================================== */
interface Opt {
  v: string;
  l: string;
}
interface Fld {
  key: string;
  label: string;
  kind: "text" | "number" | "date" | "select";
  options?: Opt[] | ((d: Data) => Opt[]);
  required?: boolean;
  full?: boolean;
  /** cannot be changed once the record exists */
  lock?: boolean;
}
interface Col {
  key: string;
  label: string;
  kind?: "money" | "date" | "status" | "num" | "pct" | "mono" | "bar";
  get?: (r: Row, d: Data) => Val;
  right?: boolean;
}
interface Act {
  label: string;
  icon: ReactNode;
  show: (r: Row) => boolean;
  exec: (r: Row, d: Data, v: Record<string, Val>) => Promise<string>;
  prompt?: { title: string; submit: string; fields: Fld[]; init: (r: Row, d: Data) => Record<string, Val> };
}
interface Tile {
  label: string;
  value: string;
  note?: string;
}
type PageKind = "grid" | "cash" | "recon" | "pl" | "bs" | "tax";
interface Entity {
  id: string;
  src: string;
  title: string;
  subtitle: string;
  noun: string;
  page: PageKind;
  cols: Col[];
  fields: Fld[];
  acts: Act[];
  readOnly?: boolean;
  /** statuses in which a record may still be edited or deleted */
  editable?: string[];
  filterKey?: string;
  filterLabel?: string;
  /** values a new record starts with */
  defaults?: (d: Data) => Record<string, Val>;
  /** values the edit form starts with (may call the API for details the list does not carry) */
  edit?: (r: Row, d: Data) => Promise<Record<string, Val>>;
  save?: (v: Record<string, Val>, d: Data, existing: Row | null) => Promise<string>;
  remove?: (r: Row, d: Data) => Promise<string>;
  /** collections to refresh after a change */
  touch?: string[];
  validate?: (v: Record<string, Val>, d: Data, existing: Row | null) => string | null;
  summary?: (list: Row[], d: Data) => Tile[];
  detail?: (r: Row, d: Data) => ReactNode;
}

/* ---------- helpers ---------- */
let CUR = "NGN";
const setCurrency = (c?: string) => {
  if (c) CUR = c;
};
const fmtMoney = (v: number, compact: boolean): string => {
  try {
    const opts: Record<string, unknown> = { style: "currency", currency: CUR, maximumFractionDigits: compact ? 1 : 0 };
    if (compact) opts.notation = "compact";
    return new Intl.NumberFormat(CUR === "NGN" ? "en-NG" : undefined, opts as Intl.NumberFormatOptions).format(v);
  } catch {
    return `${CUR} ${Math.round(v).toLocaleString()}`;
  }
};
const money = (v: number) => fmtMoney(Math.round(v) || 0, false);
const compactMoney = (v: number) => fmtMoney(v || 0, true);
const pad2 = (n: number) => String(n).padStart(2, "0");
const iso = (dt: Date) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
const parseD = (s: string) => {
  const [y, m, dd] = s.split("-").map(Number);
  return new Date(y || 0, (m || 1) - 1, dd || 1);
};
const addDays = (s: string, n: number) => {
  const dt = parseD(s);
  dt.setDate(dt.getDate() + n);
  return iso(dt);
};
const off = (n: number) => addDays(iso(new Date()), n);
const monthStart = (back: number) => {
  const dt = new Date();
  return iso(new Date(dt.getFullYear(), dt.getMonth() - back, 1));
};
const fdate = (s: string) => (s ? parseD(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");
const num = (v: Val | undefined | null): number => (typeof v === "number" ? v : Number(v) || 0);
const rows = (d: Data, k: string): Row[] => d[k] ?? [];
const sum = (list: Row[], f: (r: Row) => number) => list.reduce((t, r) => t + f(r), 0);
const opts = (...a: string[]): Opt[] => a.map((v) => ({ v, l: v }));
const cap = (s: string) => `${s[0]?.toUpperCase() ?? ""}${s.slice(1)}`;
const nameOf = (d: Data, k: string, id: Val | undefined) => String(rows(d, k).find((r) => r.id === String(id))?.name ?? id ?? "");
const errText = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong.");
const pctChange = (cur: number, prev: number) => (prev === 0 ? (cur === 0 ? "" : "new") : `${cur >= prev ? "+" : ""}${Math.round(((cur - prev) / Math.abs(prev)) * 100)}%`);

const TONE: Record<string, string> = {
  Paid: "green",
  Posted: "green",
  Receipt: "green",
  Active: "green",
  "On track": "green",
  Balanced: "green",
  Revenue: "green",
  Sent: "blue",
  Approved: "blue",
  Payment: "blue",
  Asset: "blue",
  "Partially paid": "amber",
  "Near limit": "amber",
  Liability: "amber",
  Disputed: "amber",
  Overdue: "red",
  "Over budget": "red",
  "Out of balance": "red",
  Expense: "red",
};
const Pill: React.FC<{ text: string }> = ({ text }) => <span className={`${styles.acPill} ${styles["tone_" + (TONE[text] || "grey")]}`}>{text}</span>;

/* ---------- document helpers (figures come from the server; nothing is recomputed here) ---------- */
const doc = (r: Row) => ({ total: num(r.total) });
const acctRow = (d: Data, code: string) => rows(d, "accounts").find((a) => a.id === code);
const acctLabel = (d: Data, code: Val | undefined) => {
  const a = acctRow(d, String(code));
  return a ? `${a.id} · ${String(a.name)}` : String(code ?? "");
};
const docBalance = (r: Row) => doc(r).total - num(r.paid);
const effStatus = (r: Row): string => (["Sent", "Partially paid", "Approved"].includes(String(r.status)) && String(r.due) < off(0) ? "Overdue" : String(r.status));
const OPEN_INV = ["Sent", "Partially paid"];
const OPEN_BILL = ["Approved", "Partially paid"];

/* ---------- action builders ---------- */
const LEDGER_SET = ["accounts", "banks", "ledger", "trial", "budgets"];
const reasonPrompt = (title: string): Act["prompt"] => ({ title, submit: "Confirm", fields: [{ key: "reason", label: "Reason", kind: "text", required: true, full: true }], init: () => ({ reason: "" }) });
const toggleActs = (on: (r: Row, flag: boolean) => Promise<string>): Act[] => [
  { label: "Deactivate", icon: <Dismiss20Regular />, show: (r) => r.status === "Active", exec: (r) => on(r, false) },
  { label: "Activate", icon: <Checkmark20Regular />, show: (r) => r.status === "Inactive", exec: (r) => on(r, true) },
];
const positive = (key: string, label: string) => (v: Record<string, Val>) => (num(v[key]) > 0 ? null : `${label} must be greater than zero.`);
const acctOpts = (...types: string[]) => (d: Data): Opt[] =>
  rows(d, "accounts")
    .filter((a) => types.includes(String(a.type)) && a.status === "Active")
    .map((a) => ({ v: a.id, l: `${a.id} · ${String(a.name)}` }));
const bankOpts = (d: Data): Opt[] => rows(d, "banks").map((b) => ({ v: b.id, l: String(b.name) }));
const taxOpts = (d: Data): Opt[] =>
  rows(d, "taxes")
    .filter((t) => t.status === "Active" && t.type !== "WHT")
    .map((t) => ({ v: String(t._id), l: `${String(t.name)} (${num(t.rate)}%)` }));
const partyOpts = (key: string) => (d: Data): Opt[] =>
  rows(d, key)
    .filter((c) => c.status === "Active")
    .map((c) => ({ v: c.id, l: String(c.name) }));
const defaultTax = (d: Data) => String(rows(d, "taxes").find((t) => t.type === "VAT" && t.status === "Active")?._id ?? "");

const settle = (kind: "Receipt" | "Payment", openStates: string[]): Act => ({
  label: kind === "Receipt" ? "Record payment" : "Pay bill",
  icon: <Payment20Regular />,
  show: (r) => openStates.includes(String(r.status)),
  prompt: {
    title: kind === "Receipt" ? "Record payment received" : "Pay bill",
    submit: kind === "Receipt" ? "Record payment" : "Pay",
    fields: [
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
      { key: "bank", label: kind === "Receipt" ? "Deposit to" : "Pay from", kind: "select", options: bankOpts, required: true },
      { key: "method", label: "Method", kind: "select", options: opts("Bank transfer", "Cash", "Card", "Cheque"), required: true },
    ],
    init: (r, d) => ({ date: off(0), amount: Math.round(docBalance(r) * 100) / 100, bank: rows(d, "banks")[0]?.id ?? "", method: "Bank transfer" }),
  },
  exec: async (r, d, v) => {
    const amount = num(v.amount);
    const bal = docBalance(r);
    if (amount <= 0) throw new Error("Amount must be greater than zero.");
    if (amount > bal + 0.005) throw new Error(`Amount is more than the ${money(bal)} still ${kind === "Receipt" ? "owed" : "due"}.`);
    return kind === "Receipt" ? cmd.receive(r, v, d) : cmd.pay(r, v, d);
  },
});

/** Payments applied to one invoice or bill, fetched when the row is opened. */
const DocPayments: React.FC<{ row: Row; kind: "invoice" | "bill"; rev: number }> = ({ row, kind, rev }) => {
  const [list, setList] = useState<{ date: string; method: string; amount: number }[] | null>(null);
  useEffect(() => {
    let live = true;
    setList(null);
    (kind === "invoice" ? invoiceDetail(num(row._id)) : billDetail(num(row._id)))
      .then((x) => live && setList(x))
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [row._id, kind, rev, row.paid]);
  return (
    <div className={styles.acLines}>
      <div className={styles.acLinesHead}>Payments</div>
      {list === null && <div className={styles.acHint}>Loading…</div>}
      {list !== null && list.length === 0 && <div className={styles.acHint}>No payments yet.</div>}
      {(list ?? []).map((p, i) => (
        <div key={i} className={styles.acLineRow}>
          <span>
            {fdate(p.date)} · {p.method}
          </span>
          <span>{money(p.amount)}</span>
        </div>
      ))}
    </div>
  );
};

const JournalLines: React.FC<{ row: Row; rev: number }> = ({ row, rev }) => {
  const [list, setList] = useState<{ account: string; debit: number; credit: number; memo: string }[] | null>(null);
  useEffect(() => {
    let live = true;
    setList(null);
    journalLines(num(row._id))
      .then((x) => live && setList(x))
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [row._id, rev, row.status]);
  return (
    <div className={styles.acLines}>
      <div className={styles.acLinesHead}>Lines</div>
      {list === null && <div className={styles.acHint}>Loading…</div>}
      {(list ?? []).map((l, i) => (
        <div key={i} className={styles.acLineRow}>
          <span>{l.account}</span>
          <span>{l.debit ? `Dr ${money(l.debit)}` : `Cr ${money(l.credit)}`}</span>
        </div>
      ))}
    </div>
  );
};

/* ---------- page definitions ---------- */
const docCols = (partyKey: string, partyLabel: string, partyField: string): Col[] => [
  { key: "id", label: "Number", kind: "mono" },
  { key: partyField, label: partyLabel, get: (r, d) => nameOf(d, partyKey, r[partyField]) },
  { key: "date", label: "Date", kind: "date" },
  { key: "due", label: "Due", kind: "date" },
  { key: "total", label: "Total", kind: "money", right: true },
  { key: "paid", label: "Paid", kind: "money", right: true },
  { key: "balance", label: "Balance", kind: "money", right: true, get: (r) => docBalance(r) },
  { key: "status", label: "Status", kind: "status", get: (r) => effStatus(r) },
];

const CTX = { rev: 0 };

const ENTS: Entity[] = [
  { id: "Cashflow", src: "banks", title: "Cashflow", subtitle: "Where cash is today, what came in and went out, and what is expected.", noun: "", page: "cash", cols: [], fields: [], acts: [] },
  {
    id: "Budget",
    src: "budgets",
    title: "Budget",
    subtitle: "Spending limits from the active budget, tracked against the ledger for the year so far. Budget versions are managed under Planning.",
    noun: "budget line",
    page: "grid",
    readOnly: true,
    cols: [
      { key: "account", label: "Account", get: (r, d) => acctLabel(d, r.account) },
      { key: "amount", label: "Budget", kind: "money", right: true },
      { key: "actual", label: "Actual", kind: "money", right: true },
      { key: "remaining", label: "Remaining", kind: "money", right: true },
      { key: "used", label: "Used", kind: "bar" },
      { key: "state", label: "Status", kind: "status" },
    ],
    fields: [],
    filterKey: "state",
    filterLabel: "All statuses",
    summary: (list) => {
      const bud = sum(list, (r) => num(r.amount));
      const act = sum(list, (r) => num(r.actual));
      return [
        { label: "Total budget", value: money(bud), note: `${list.length} lines` },
        { label: "Spent so far", value: money(act), note: bud ? `${Math.round((act / bud) * 100)}% of budget` : "" },
        { label: "Remaining", value: money(bud - act), note: "Across all lines" },
        { label: "Over budget", value: String(list.filter((r) => r.state === "Over budget").length), note: "Lines past their limit" },
      ];
    },
    acts: [],
  },
  {
    id: "Invoices",
    src: "invoices",
    title: "Invoices",
    subtitle: "Bills you send to customers. Sending an invoice posts it to the ledger.",
    noun: "invoice",
    page: "grid",
    editable: ["Draft"],
    touch: ["invoices", "payments", "customers", ...LEDGER_SET],
    cols: docCols("customers", "Customer", "customer"),
    fields: [
      { key: "customer", label: "Customer", kind: "select", options: partyOpts("customers"), required: true, full: true },
      { key: "date", label: "Invoice date", kind: "date", required: true },
      { key: "due", label: "Due date", kind: "date", required: true },
      { key: "account", label: "Revenue account", kind: "select", options: acctOpts("Revenue"), required: true, full: true },
      { key: "description", label: "Description", kind: "text", full: true },
      { key: "net", label: "Amount (excl. tax)", kind: "number", required: true },
      { key: "taxRate", label: "Tax", kind: "select", options: taxOpts },
    ],
    defaults: (d) => ({ date: off(0), due: off(30), taxRate: defaultTax(d) }),
    edit: invoiceEdit,
    save: cmd.invoiceSave,
    remove: (r) => cmd.invoiceDelete(r),
    validate: (v) => (num(v.net) <= 0 ? "Amount must be greater than zero." : String(v.due) < String(v.date) ? "Due date can't be before the invoice date." : null),
    summary: (list) => {
      const open = list.filter((r) => OPEN_INV.includes(String(r.status)));
      const over = open.filter((r) => effStatus(r) === "Overdue");
      return [
        { label: "Outstanding", value: money(sum(open, docBalance)), note: `${open.length} unpaid invoices` },
        { label: "Overdue", value: money(sum(over, docBalance)), note: `${over.length} past due` },
        { label: "Paid", value: money(sum(list, (r) => num(r.paid))), note: "Collected to date" },
        { label: "Drafts", value: String(list.filter((r) => r.status === "Draft").length), note: "Not yet sent" },
      ];
    },
    detail: (r) => <DocPayments row={r} kind="invoice" rev={CTX.rev} />,
    acts: [
      { label: "Send", icon: <Send20Regular />, show: (r) => r.status === "Draft", exec: (r) => cmd.invoiceSend(r) },
      settle("Receipt", OPEN_INV),
      { label: "Void", icon: <Prohibited20Regular />, show: (r) => ["Draft", "Sent"].includes(String(r.status)) && num(r.paid) === 0, prompt: { ...reasonPrompt("Void invoice")!, submit: "Void" }, exec: (r, _d, v) => cmd.invoiceVoid(r, v) },
    ],
  },
  {
    id: "Payments",
    src: "payments",
    title: "Payments",
    subtitle: "Money received from customers and paid to vendors.",
    noun: "payment",
    page: "grid",
    readOnly: true,
    cols: [
      { key: "id", label: "Payment", kind: "mono" },
      { key: "date", label: "Date", kind: "date" },
      { key: "type", label: "Type", kind: "status" },
      { key: "party", label: "Party" },
      { key: "ref", label: "Reference", kind: "mono" },
      { key: "method", label: "Method" },
      { key: "bank", label: "Bank", get: (r, d) => nameOf(d, "banks", r.bank) },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [],
    filterKey: "type",
    filterLabel: "Receipts and payments",
    summary: (list) => {
      const ins = sum(list.filter((r) => r.type === "Receipt"), (r) => num(r.amount));
      const outs = sum(list.filter((r) => r.type === "Payment"), (r) => num(r.amount));
      return [
        { label: "Received", value: money(ins), note: "From customers" },
        { label: "Paid out", value: money(outs), note: "To vendors" },
        { label: "Net", value: money(ins - outs), note: "Received less paid" },
      ];
    },
    acts: [],
  },
  {
    id: "Payables",
    src: "payables",
    title: "Payables",
    subtitle: "Bills from vendors. Approving a bill posts it to the ledger.",
    noun: "bill",
    page: "grid",
    editable: ["Draft"],
    touch: ["payables", "payments", "vendors", ...LEDGER_SET],
    cols: docCols("vendors", "Vendor", "vendor"),
    fields: [
      { key: "vendor", label: "Vendor", kind: "select", options: partyOpts("vendors"), required: true, full: true },
      { key: "number", label: "Vendor invoice no.", kind: "text", required: true },
      { key: "date", label: "Bill date", kind: "date", required: true },
      { key: "due", label: "Due date", kind: "date", required: true },
      { key: "account", label: "Expense account", kind: "select", options: acctOpts("Expense", "Cost of sales"), required: true, full: true },
      { key: "description", label: "Description", kind: "text", full: true },
      { key: "net", label: "Amount (excl. tax)", kind: "number", required: true },
      { key: "taxRate", label: "Tax", kind: "select", options: taxOpts },
    ],
    defaults: (d) => ({ date: off(0), due: off(30), taxRate: defaultTax(d) }),
    edit: billEdit,
    save: cmd.billSave,
    remove: (r) => cmd.billDelete(r),
    validate: (v) => (num(v.net) <= 0 ? "Amount must be greater than zero." : String(v.due) < String(v.date) ? "Due date can't be before the bill date." : null),
    summary: (list) => {
      const open = list.filter((r) => OPEN_BILL.includes(String(r.status)));
      const over = open.filter((r) => effStatus(r) === "Overdue");
      const week = open.filter((r) => String(r.due) >= off(0) && String(r.due) <= off(7));
      return [
        { label: "Owed to vendors", value: money(sum(open, docBalance)), note: `${open.length} open bills` },
        { label: "Overdue", value: money(sum(over, docBalance)), note: `${over.length} past due` },
        { label: "Due in 7 days", value: money(sum(week, docBalance)), note: `${week.length} bills` },
        { label: "To approve", value: String(list.filter((r) => r.status === "Draft").length), note: "Draft bills" },
      ];
    },
    detail: (r) => <DocPayments row={r} kind="bill" rev={CTX.rev} />,
    acts: [
      { label: "Approve", icon: <Checkmark20Regular />, show: (r) => r.status === "Draft", exec: (r) => cmd.billApprove(r) },
      settle("Payment", OPEN_BILL),
      { label: "Void", icon: <Prohibited20Regular />, show: (r) => ["Draft", "Approved"].includes(String(r.status)) && num(r.paid) === 0, prompt: { ...reasonPrompt("Void bill")!, submit: "Void" }, exec: (r, _d, v) => cmd.billVoid(r, v) },
    ],
  },
  {
    id: "Expenses",
    src: "expenses",
    title: "Expenses",
    subtitle: "Direct spending paid from a bank or cash account.",
    noun: "expense",
    page: "grid",
    editable: ["Draft"],
    touch: ["expenses", ...LEDGER_SET],
    cols: [
      { key: "id", label: "Expense", kind: "mono" },
      { key: "date", label: "Date", kind: "date" },
      { key: "payee", label: "Payee" },
      { key: "account", label: "Account", get: (r, d) => acctLabel(d, r.account) },
      { key: "bank", label: "Paid from", get: (r, d) => nameOf(d, "banks", r.bank) },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "payee", label: "Payee", kind: "text", required: true },
      { key: "account", label: "Expense account", kind: "select", options: acctOpts("Expense", "Cost of sales"), required: true, full: true },
      { key: "bank", label: "Paid from", kind: "select", options: bankOpts, required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
      { key: "memo", label: "Note", kind: "text", full: true },
    ],
    save: cmd.expenseSave,
    remove: (r) => cmd.expenseDelete(r),
    validate: positive("amount", "Amount"),
    summary: (list) => {
      const live = list.filter((r) => r.status === "Posted");
      const thisMonth = live.filter((r) => String(r.date) >= monthStart(0));
      return [
        { label: "This month", value: money(sum(thisMonth, (r) => num(r.amount))), note: `${thisMonth.length} expenses` },
        { label: "Posted in total", value: money(sum(live, (r) => num(r.amount))), note: `${live.length} expenses` },
        { label: "Drafts", value: String(list.filter((r) => r.status === "Draft").length), note: "Not yet posted" },
      ];
    },
    acts: [
      { label: "Post", icon: <Checkmark20Regular />, show: (r) => r.status === "Draft", exec: (r) => cmd.expensePost(r) },
      { label: "Void", icon: <Prohibited20Regular />, show: (r) => r.status === "Posted", exec: (r) => cmd.expenseVoid(r) },
    ],
  },
  {
    id: "Journal",
    src: "journal",
    title: "Journal entries",
    subtitle: "Every journal in the ledger. Manual adjustments here have one debit and one credit.",
    noun: "journal entry",
    page: "grid",
    editable: ["Draft"],
    touch: ["journal", ...LEDGER_SET],
    cols: [
      { key: "id", label: "Entry", kind: "mono" },
      { key: "date", label: "Date", kind: "date" },
      { key: "memo", label: "Memo" },
      { key: "source", label: "Source" },
      { key: "amount", label: "Amount", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "date", label: "Date", kind: "date", required: true },
      { key: "memo", label: "Memo", kind: "text", required: true },
      { key: "debit", label: "Debit account", kind: "select", options: acctOpts("Asset", "Liability", "Equity", "Revenue", "Cost of sales", "Expense"), required: true },
      { key: "credit", label: "Credit account", kind: "select", options: acctOpts("Asset", "Liability", "Equity", "Revenue", "Cost of sales", "Expense"), required: true },
      { key: "amount", label: "Amount", kind: "number", required: true },
    ],
    defaults: () => ({ date: off(0) }),
    edit: journalEdit,
    save: cmd.journalSave,
    remove: (r) => cmd.journalDelete(r),
    validate: (v) => (num(v.amount) <= 0 ? "Amount must be greater than zero." : v.debit === v.credit ? "Debit and credit accounts must be different." : null),
    detail: (r) => <JournalLines row={r} rev={CTX.rev} />,
    filterKey: "source",
    filterLabel: "All sources",
    acts: [
      { label: "Post", icon: <Checkmark20Regular />, show: (r) => r.status === "Draft", exec: (r) => cmd.journalPost(r) },
      {
        label: "Reverse",
        icon: <ArrowUndo20Regular />,
        show: (r) => r.status === "Posted",
        prompt: {
          title: "Reverse entry",
          submit: "Reverse",
          fields: [
            { key: "date", label: "Reversal date", kind: "date", required: true },
            { key: "reason", label: "Reason", kind: "text", full: true },
          ],
          init: () => ({ date: off(0), reason: "" }),
        },
        exec: (r, _d, v) => cmd.journalReverse(r, v),
      },
    ],
  },
  {
    id: "Customers",
    src: "customers",
    title: "Customers",
    subtitle: "Who you invoice and how much they owe.",
    noun: "customer",
    page: "grid",
    touch: ["customers"],
    cols: [
      { key: "id", label: "Customer", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "terms", label: "Terms" },
      { key: "owes", label: "Outstanding", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "email", label: "Email", kind: "text" },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "terms", label: "Payment terms", kind: "select", options: opts("Due on receipt", "Net 15", "Net 30", "Net 45"), required: true },
    ],
    defaults: () => ({ terms: "Net 30" }),
    save: cmd.customerSave,
    acts: toggleActs((r, on) => cmd.customerActive(r, on)),
  },
  {
    id: "Vendors",
    src: "vendors",
    title: "Vendors",
    subtitle: "Who you buy from and how much you owe them.",
    noun: "vendor",
    page: "grid",
    touch: ["vendors"],
    cols: [
      { key: "id", label: "Vendor", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "terms", label: "Terms" },
      { key: "owed", label: "We owe", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "email", label: "Email", kind: "text" },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "terms", label: "Payment terms", kind: "select", options: opts("Due on receipt", "Net 15", "Net 30", "Net 45"), required: true },
    ],
    defaults: () => ({ terms: "Net 30" }),
    save: cmd.vendorSave,
    acts: toggleActs((r, on) => cmd.vendorActive(r, on)),
  },
  {
    id: "Banks",
    src: "banks",
    title: "Bank accounts",
    subtitle: "Bank and cash accounts. Balances come from the ledger.",
    noun: "bank account",
    page: "grid",
    touch: ["banks", "accounts"],
    cols: [
      { key: "name", label: "Name" },
      { key: "bank", label: "Bank" },
      { key: "number", label: "Account number", kind: "mono" },
      { key: "gl", label: "Ledger account", get: (r, d) => acctLabel(d, r.gl) },
      { key: "balance", label: "Balance", kind: "money", right: true },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "bank", label: "Bank", kind: "text", required: true },
      { key: "number", label: "Account number", kind: "text" },
      {
        key: "gl",
        label: "Ledger account",
        kind: "select",
        options: (d) => rows(d, "accounts").filter((a) => num(a.isBank) === 1 && a.status === "Active").map((a) => ({ v: a.id, l: `${a.id} · ${String(a.name)}` })),
        required: true,
        full: true,
        lock: true,
      },
    ],
    save: cmd.bankSave,
    remove: (r) => cmd.bankDelete(r),
    validate: (v, d, ex) => (rows(d, "banks").some((b) => b.gl === v.gl && b.id !== ex?.id) ? "That ledger account already belongs to another bank account." : null),
    summary: (list) => [{ label: "Total cash", value: money(sum(list, (r) => num(r.balance))), note: `${list.length} accounts` }],
    acts: [],
  },
  { id: "Reconciliation", src: "banks", title: "Bank reconciliation", subtitle: "Tick the lines on your bank statement until the difference is zero.", noun: "", page: "recon", cols: [], fields: [], acts: [] },
  {
    id: "Accounts",
    src: "accounts",
    title: "Chart of accounts",
    subtitle: "Every account the ledger posts to.",
    noun: "account",
    page: "grid",
    touch: ["accounts", "groups", "banks"],
    cols: [
      { key: "id", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type", kind: "status" },
      { key: "group", label: "Group" },
      { key: "balance", label: "Balance today", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true, lock: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "group", label: "Account group", kind: "select", options: (d) => rows(d, "groups").map((g) => ({ v: g.id, l: String(g.label) })), required: true, full: true },
    ],
    edit: async (r) => ({ code: r.id, name: String(r.name), group: String(r.groupId) }),
    save: cmd.accountSave,
    validate: (v, d, ex) => (!ex && rows(d, "accounts").some((a) => a.id === String(v.code)) ? `Account ${String(v.code)} already exists.` : null),
    filterKey: "type",
    filterLabel: "All types",
    acts: toggleActs((r, on) => cmd.accountActive(r, on)),
  },
  {
    id: "Ledger",
    src: "ledger",
    title: "General ledger",
    subtitle: "The latest 500 postings, newest first.",
    noun: "posting",
    page: "grid",
    readOnly: true,
    cols: [
      { key: "date", label: "Date", kind: "date" },
      { key: "ref", label: "Reference", kind: "mono" },
      { key: "account", label: "Account" },
      { key: "memo", label: "Memo" },
      { key: "dr", label: "Debit", kind: "money", right: true },
      { key: "cr", label: "Credit", kind: "money", right: true },
    ],
    fields: [],
    filterKey: "account",
    filterLabel: "All accounts",
    summary: (list) => [
      { label: "Debits", value: money(sum(list, (r) => num(r.dr))), note: `${list.length} postings` },
      { label: "Credits", value: money(sum(list, (r) => num(r.cr))), note: "Always equal to debits" },
    ],
    acts: [],
  },
  {
    id: "Trial",
    src: "trial",
    title: "Trial balance",
    subtitle: "Debit and credit balance of every account as of today.",
    noun: "account",
    page: "grid",
    readOnly: true,
    cols: [
      { key: "id", label: "Code", kind: "mono" },
      { key: "name", label: "Account" },
      { key: "type", label: "Type", kind: "status" },
      { key: "debit", label: "Debit", kind: "money", right: true },
      { key: "credit", label: "Credit", kind: "money", right: true },
    ],
    fields: [],
    filterKey: "type",
    filterLabel: "All types",
    summary: (list) => {
      const dr = sum(list, (r) => num(r.debit));
      const cr = sum(list, (r) => num(r.credit));
      return [
        { label: "Total debits", value: money(dr) },
        { label: "Total credits", value: money(cr) },
        { label: "Difference", value: money(dr - cr), note: Math.abs(dr - cr) < 1 ? "Balanced" : "Out of balance" },
      ];
    },
    acts: [],
  },
  {
    id: "Assets",
    src: "assets",
    title: "Fixed assets",
    subtitle: "Assets you own. Depreciation is posted to the ledger by the monthly run.",
    noun: "asset",
    page: "grid",
    touch: ["assets", ...LEDGER_SET],
    cols: [
      { key: "id", label: "Asset", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "category", label: "Category" },
      { key: "date", label: "Purchased", kind: "date" },
      { key: "cost", label: "Cost", kind: "money", right: true },
      { key: "life", label: "Life (years)", kind: "num", right: true },
      { key: "monthly", label: "Per month", kind: "money", right: true },
      { key: "accum", label: "Depreciation to date", kind: "money", right: true },
      { key: "nbv", label: "Book value", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "category", label: "Category", kind: "select", options: (d) => rows(d, "assetCats").map((c) => ({ v: String(c._id), l: String(c.name) })), required: true, lock: true },
      { key: "date", label: "Purchase date", kind: "date", required: true, lock: true },
      { key: "cost", label: "Cost", kind: "number", required: true, lock: true },
      { key: "life", label: "Useful life (years, optional)", kind: "number", lock: true },
      { key: "bank", label: "Paid from (optional)", kind: "select", options: bankOpts, full: true, lock: true },
    ],
    defaults: () => ({ date: off(0) }),
    edit: async (r) => ({ name: String(r.name), category: String(r.catId), date: String(r.date), cost: num(r.cost), life: num(r.life), bank: "" }),
    save: cmd.assetSave,
    validate: (v, _d, ex) => (ex ? null : num(v.cost) <= 0 ? "Cost must be greater than zero." : null),
    summary: (list) => {
      const cost = sum(list, (r) => num(r.cost));
      const dep = sum(list, (r) => num(r.accum));
      return [
        { label: "Total cost", value: money(cost), note: `${list.length} assets` },
        { label: "Depreciation to date", value: money(dep) },
        { label: "Book value", value: money(sum(list, (r) => num(r.nbv))) },
      ];
    },
    acts: [],
  },
  {
    id: "TaxRates",
    src: "taxes",
    title: "Tax rates",
    subtitle: "Rates offered on invoices and bills.",
    noun: "tax rate",
    page: "grid",
    touch: ["taxes"],
    cols: [
      { key: "id", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "rate", label: "Rate", kind: "pct", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true, lock: true },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "type", label: "Type", kind: "select", options: opts("VAT", "WHT", "CIT", "PAYE", "Stamp Duty", "Other"), required: true },
      { key: "rate", label: "Rate (%)", kind: "number", required: true },
      { key: "account", label: "Tax account", kind: "select", options: acctOpts("Liability", "Asset"), full: true, lock: true },
    ],
    defaults: () => ({ type: "VAT" }),
    edit: async (r) => ({ code: r.id, name: String(r.name), type: String(r.type), rate: num(r.rate), account: "" }),
    save: cmd.taxSave,
    validate: (v, _d, ex) => (num(v.rate) < 0 || num(v.rate) > 100 ? "Rate must be between 0 and 100." : !ex && !v.account ? "Choose the account this tax posts to." : null),
    acts: toggleActs((r, on) => cmd.taxActive(r, on)),
  },
  {
    id: "Audit",
    src: "audit",
    title: "Audit log",
    subtitle: "Every change made in the books, newest first.",
    noun: "entry",
    page: "grid",
    readOnly: true,
    cols: [
      { key: "when", label: "When" },
      { key: "user", label: "User" },
      { key: "entity", label: "Table" },
      { key: "record", label: "Record", kind: "mono" },
      { key: "action", label: "Action" },
      { key: "detail", label: "Detail" },
    ],
    fields: [],
    acts: [],
  },
  { id: "P&L", src: "accounts", title: "Profit & Loss", subtitle: "Revenue, costs and profit for a period, compared with the one before.", noun: "", page: "pl", cols: [], fields: [], acts: [] },
  { id: "Balance Sheet", src: "accounts", title: "Balance Sheet", subtitle: "What the business owns, owes and is worth on a date.", noun: "", page: "bs", cols: [], fields: [], acts: [] },
  { id: "Tax", src: "invoices", title: "Tax Reports", subtitle: "VAT collected on sales and paid on purchases.", noun: "", page: "tax", cols: [], fields: [], acts: [] },
];

const UseBar: React.FC<{ pct: number }> = ({ pct }) => (
  <span className={styles.acUse}>
    <span className={styles.acUseTrack}>
      <span className={`${styles.acUseFill} ${pct > 100 ? styles.acUseOver : pct >= 85 ? styles.acUseWarn : ""}`} style={{ width: `${Math.min(pct, 100)}%` }} />
    </span>
    <span>{pct}%</span>
  </span>
);
/* ---------- charts ---------- */
const DualColumns: React.FC<{ items: { label: string; a: number; b: number }[]; aName: string; bName: string }> = ({ items, aName, bName }) => {
  const max = Math.max(...items.map((i) => i.a), ...items.map((i) => i.b), 1);
  if (items.every((i) => i.a === 0 && i.b === 0)) return <div className={styles.acEmpty}>No cash movement in this period.</div>;
  return (
    <div>
      <div className={styles.acLegend}>
        <span>
          <i className={styles.acDotA} />
          {aName}
        </span>
        <span>
          <i className={styles.acDotB} />
          {bName}
        </span>
      </div>
      <div className={styles.acChart} role="img" aria-label={items.map((i) => `${i.label}: ${aName} ${money(i.a)}, ${bName} ${money(i.b)}`).join("; ")}>
        {items.map((i) => (
          <div key={i.label} className={styles.acChartCol}>
            <div className={styles.acChartBars}>
              <span className={`${styles.acChartBar} ${styles.acBarA}`} style={{ height: i.a > 0 ? `${Math.max((i.a / max) * 100, 3)}%` : "0" }} title={`${aName}: ${money(i.a)}`} />
              <span className={`${styles.acChartBar} ${styles.acBarB}`} style={{ height: i.b > 0 ? `${Math.max((i.b / max) * 100, 3)}%` : "0" }} title={`${bName}: ${money(i.b)}`} />
            </div>
            <span className={styles.acChartLabel}>{i.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const HBars: React.FC<{ items: { label: string; value: number }[] }> = ({ items }) => {
  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);
  return (
    <div className={styles.acHBars}>
      {items.map((i) => (
        <div key={i.label} className={styles.acHRow}>
          <span className={styles.acHLabel}>{i.label}</span>
          <span className={styles.acHTrack}>
            <span className={styles.acHFill} style={{ width: `${(Math.max(i.value, 0) / max) * 100}%` }} />
          </span>
          <span className={styles.acHVal}>{money(i.value)}</span>
        </div>
      ))}
    </div>
  );
};

const Panel: React.FC<{ title: string; note?: string; children: ReactNode; wide?: boolean }> = ({ title, note, children, wide }) => (
  <section className={`${styles.acPanel} ${wide ? styles.acWide : ""}`}>
    <h3 className={styles.acPanelTitle}>{title}</h3>
    {note && <p className={styles.acPanelNote}>{note}</p>}
    {children}
  </section>
);
const MiniList: React.FC<{ items: { id: string; who: string; due: string; amount: number; late: boolean }[]; empty: string }> = ({ items, empty }) => (
  <ul className={styles.acMini}>
    {items.slice(0, 6).map((i) => (
      <li key={i.id}>
        <span>
          <strong>{i.who}</strong>
          <small>
            {i.id} · due {fdate(i.due)}
          </small>
        </span>
        <span className={i.late ? styles.acNeg : undefined}>{money(i.amount)}</span>
      </li>
    ))}
    {items.length === 0 && <li className={styles.acEmpty}>{empty}</li>}
  </ul>
);

/* ---------- record form (also used for action prompts) ---------- */
const RecordDialog: React.FC<{
  title: string;
  submit: string;
  fields: Fld[];
  data: Data;
  initial: Record<string, Val>;
  existing: boolean;
  onClose: () => void;
  onSave: (v: Record<string, Val>) => Promise<string | null>;
}> = ({ title, submit, fields, data, initial, existing, onClose, onSave }) => {
  const [v, setV] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    fields.forEach((f) => {
      const cur = initial[f.key];
      o[f.key] = cur !== undefined ? String(cur) : f.kind === "date" ? off(0) : "";
    });
    return o;
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (busy) return;
    const missing = fields.find((f) => f.required && !(v[f.key] ?? "").trim());
    if (missing) return setErr(`${missing.label} is required.`);
    const out: Record<string, Val> = {};
    fields.forEach((f) => {
      const raw = (v[f.key] ?? "").trim();
      out[f.key] = f.kind === "number" ? Number(raw) || 0 : raw;
    });
    setBusy(true);
    setErr(null);
    const bad = await onSave(out);
    setBusy(false);
    if (bad) setErr(bad);
  };

  return (
    <Dialog open onOpenChange={(_, dd) => !dd.open && onClose()}>
      <DialogSurface className={styles.acDialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button size="small" appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent>
            <div className={styles.acForm}>
              {fields.map((f) => {
                const options = typeof f.options === "function" ? f.options(data) : f.options ?? [];
                return (
                  <div key={f.key} className={f.full ? styles.acFormFull : undefined}>
                    <Field label={f.label} required={!!f.required}>
                      {f.kind === "select" ? (
                        <Select disabled={f.lock && existing} value={v[f.key] ?? ""} onChange={(_, dd) => setV({ ...v, [f.key]: dd.value })}>
                          <option value="">Select…</option>
                          {options.map((o) => (
                            <option key={o.v} value={o.v}>
                              {o.l}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input disabled={f.lock && existing} type={f.kind === "number" ? "number" : f.kind === "date" ? "date" : "text"} value={v[f.key] ?? ""} onChange={(_, dd) => setV({ ...v, [f.key]: dd.value })} />
                      )}
                    </Field>
                  </div>
                );
              })}
            </div>
            {err && (
              <div className={styles.acErr} role="alert">
                {err}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button size="small">Cancel</Button>
            </DialogTrigger>
            <Button size="small" className={styles.acPrimary} appearance="primary" disabled={busy} onClick={go}>
              {busy ? "Working…" : submit}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ---------- list page ---------- */
interface PageProps {
  data: Data;
  /** refetch the named collections (all when omitted) */
  reload: (keys?: string[]) => Promise<void>;
  say: (msg: string, err?: boolean) => void;
  /** bumps after every change, so pages that fetch their own view know to refresh */
  rev: number;
}

const val = (c: Col, r: Row, d: Data): Val => (c.get ? c.get(r, d) : r[c.key] ?? "");
const show = (c: Col, v: Val): string => {
  switch (c.kind) {
    case "money":
      return money(num(v));
    case "date":
      return v ? fdate(String(v)) : "";
    case "pct":
    case "bar":
      return `${num(v)}%`;
    case "num":
      return num(v).toLocaleString("en-US");
    default:
      return String(v);
  }
};

const Tiles: React.FC<{ tiles: Tile[] }> = ({ tiles }) => (
  <div className={styles.acTiles}>
    {tiles.map((t) => (
      <div key={t.label} className={styles.acTile}>
        <span className={styles.acTileLabel}>{t.label}</span>
        <span className={styles.acTileValue}>{t.value}</span>
        {t.note ? <span className={styles.acTileNote}>{t.note === "Balanced" || t.note === "Out of balance" ? <Pill text={t.note} /> : t.note}</span> : null}
      </div>
    ))}
  </div>
);

/** Loads a view from the API and refetches when `deps` change. */
function useView<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | null; err: string | null } {
  const [state, setState] = useState<{ data: T | null; err: string | null }>({ data: null, err: null });
  useEffect(() => {
    let live = true;
    fn()
      .then((x) => live && setState({ data: x, err: null }))
      .catch((e) => live && setState((s0) => ({ data: s0.data, err: errText(e) })));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

/* ---------- loading skeleton (shown while data is on its way) ---------- */
const SkBar: React.FC<{ w?: number | string; h?: number; r?: number }> = ({ w = "100%", h = 12, r = 6 }) => (
  <span className={styles.acSk} style={{ width: w, height: h, borderRadius: r }} />
);

const Skeleton: React.FC<{ kind?: "page" | "dash" }> = ({ kind = "page" }) => (
  <div className={styles.acSkWrap} aria-busy="true" aria-live="polite" aria-label="Loading">
    {kind === "dash" ? (
      <>
        <div className={styles.acSkCol}>
          <SkBar w={110} h={10} />
          <SkBar w={320} h={26} />
          <SkBar w={260} h={11} />
        </div>
        <div className={styles.acSkGrid3}>
          {[0, 1, 2].map((i) => (
            <div className={styles.acSkCard} key={i}>
              <div className={styles.acSkRow}>
                <SkBar w={36} h={36} r={10} />
                <div className={styles.acSkCol} style={{ flex: 1 }}>
                  <SkBar w="55%" h={11} />
                  <SkBar w="35%" h={20} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.acSkTwo}>
          {[0, 1, 2, 3].map((c) => (
            <div className={styles.acSkCard} key={c}>
              <div className={styles.acSkCol}>
                <SkBar w={170} h={16} />
                <SkBar w={240} h={11} />
              </div>
              <div className={styles.acSkBars}>
                {[40, 70, 55, 90, 65, 80].map((h, i) => (
                  <SkBar key={i} w="100%" h={h} r={6} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </>
    ) : (
      <>
        <div className={styles.acSkGrid4}>
          {[0, 1, 2, 3].map((i) => (
            <div className={styles.acSkCard} key={i}>
              <div className={styles.acSkRow}>
                <SkBar w={36} h={36} r={10} />
                <SkBar w={110} h={10} />
              </div>
              <div style={{ height: 14 }} />
              <div className={styles.acSkRow}>
                <SkBar w={80} h={24} />
                <SkBar w={90} h={10} />
              </div>
            </div>
          ))}
        </div>
        <div className={styles.acSkCard}>
          <div className={styles.acSkRow}>
            <SkBar w={220} h={32} r={8} />
            <SkBar w={130} h={32} r={8} />
            <span style={{ flex: 1 }} />
            <SkBar w={110} h={32} r={8} />
          </div>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div className={styles.acSkLine} key={i}>
              <SkBar w={16} h={16} r={4} />
              <SkBar w="14%" h={12} />
              <SkBar w="24%" h={12} />
              <SkBar w="16%" h={12} />
              <SkBar w={72} h={22} r={11} />
              <SkBar w="10%" h={12} />
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

const Waiting: React.FC<{ err: string | null }> = ({ err }) =>
  err ? (
    <div className={styles.acErr} role="alert">
      {err}
    </div>
  ) : (
    <Skeleton />
  );

const EntityPage: React.FC<PageProps & { ent: Entity; list?: Row[] }> = ({ ent, data, reload, say, list: given }) => {
  const [q, setQ] = useState("");
  const [filterV, setFilterV] = useState("All");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<"new" | "edit" | null>(null);
  const [init, setInit] = useState<Record<string, Val>>({});
  const [prompt, setPrompt] = useState<Act | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => given ?? rows(data, ent.src), [given, data, ent.src]);
  const filterCol = ent.cols.find((c) => c.key === ent.filterKey) ?? ent.cols.find((c) => c.kind === "status");
  const choices = filterCol ? Array.from(new Set(list.map((r) => String(val(filterCol, r, data))))).filter(Boolean).sort() : [];

  const view = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = list.filter((r) => {
      if (filterCol && filterV !== "All" && String(val(filterCol, r, data)) !== filterV) return false;
      return !needle || ent.cols.some((c) => show(c, val(c, r, data)).toLowerCase().includes(needle));
    });
    if (sort) {
      const col = ent.cols.find((c) => c.key === sort.key);
      if (col) {
        const dir = sort.dir;
        out = [...out].sort((a, b) => {
          const x = val(col, a, data);
          const y = val(col, b, data);
          return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y))) * dir;
        });
      }
    }
    return out;
  }, [list, q, filterV, sort, ent, filterCol, data]);

  const sel = list.find((r) => r.id === selId) ?? null;
  const canChange = !ent.readOnly && !!sel && (!ent.editable || ent.editable.includes(String(sel.status)));
  const tiles = ent.summary ? ent.summary(list, data) : null;

  /** Runs one API call, refreshes what it touched, and reports the outcome. Returns an error message or null. */
  const run = async (fn: () => Promise<string>): Promise<string | null> => {
    setBusy(true);
    try {
      const msg = await fn();
      await reload(ent.touch);
      say(msg);
      return null;
    } catch (e) {
      return errText(e);
    } finally {
      setBusy(false);
    }
  };

  const runAct = async (a: Act, values: Record<string, Val>): Promise<string | null> => {
    if (!sel) return null;
    const bad = await run(() => a.exec(sel, data, values));
    if (bad && !a.prompt) say(bad, true);
    return bad;
  };

  const openNew = () => {
    setInit(ent.defaults ? ent.defaults(data) : {});
    setForm("new");
  };
  const openEdit = async () => {
    if (!sel) return;
    try {
      setInit(ent.edit ? await ent.edit(sel, data) : (sel as Record<string, Val>));
      setForm("edit");
    } catch (e) {
      say(errText(e), true);
    }
  };

  const save = async (values: Record<string, Val>): Promise<string | null> => {
    const existing = form === "edit" ? sel : null;
    const bad = ent.validate ? ent.validate(values, data, existing) : null;
    if (bad) return bad;
    if (!ent.save) return "This page can't save changes.";
    const err = await run(() => ent.save!(values, data, existing));
    if (!err) setForm(null);
    return err;
  };

  const remove = async () => {
    if (!sel || !ent.remove) return;
    const err = await run(() => ent.remove!(sel, data));
    if (err) say(err, true);
    else setSelId(null);
    setConfirmDel(false);
  };

  const exportCsv = () => {
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const lines = [ent.cols.map((c) => esc(c.label)).join(",")].concat(view.map((r) => ent.cols.map((c) => esc(show(c, val(c, r, data)))).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${ent.id.toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortBy = (key: string) => setSort((s0) => (s0 && s0.key === key ? (s0.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }));
  const moneyCol = ent.cols.find((c) => c.kind === "money" && (c.key === "total" || c.key === "amount"));
  const moneyTotal = moneyCol ? view.reduce((t, r) => t + num(val(moneyCol, r, data)), 0) : 0;
  const editable = !ent.readOnly && !!ent.save;

  return (
    <>
      {tiles && <Tiles tiles={tiles} />}
      <div className={styles.acCard}>
        <div className={styles.acPane} role="toolbar" aria-label="Actions">
          {editable && (
            <Button size="small" className={styles.acPrimary} appearance="primary" icon={<Add20Regular />} disabled={busy} onClick={openNew}>
              New
            </Button>
          )}
          {editable && (
            <Button size="small" appearance="subtle" icon={<Edit20Regular />} disabled={!canChange || busy} onClick={openEdit}>
              Edit
            </Button>
          )}
          {!ent.readOnly && !!ent.remove && (
            <Button size="small" appearance="subtle" icon={<Delete20Regular />} disabled={!canChange || busy} onClick={() => setConfirmDel(true)}>
              Delete
            </Button>
          )}
          {!ent.readOnly && ent.acts.length > 0 && <span className={styles.acSep} />}
          {ent.acts.map((a) => (
            <Button key={a.label} size="small" appearance="subtle" icon={a.icon as React.ReactElement} disabled={!sel || !a.show(sel) || busy} onClick={() => (a.prompt ? setPrompt(a) : runAct(a, {}))}>
              {a.label}
            </Button>
          ))}
          <span className={styles.acGrow} />
          <Button size="small" appearance="subtle" icon={<ArrowDownload20Regular />} onClick={exportCsv}>
            Export
          </Button>
        </div>

        <div className={styles.acFilters}>
          <SearchBox size="small" className={styles.acSearch} placeholder={`Search ${ent.title.toLowerCase()}`} value={q} onChange={(_, dd) => setQ(dd.value)} />
          {filterCol && (
            <Select size="small" className={styles.acSel} value={filterV} onChange={(_, dd) => setFilterV(dd.value)} aria-label="Filter">
              <option value="All">{ent.filterLabel ?? "All statuses"}</option>
              {choices.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
          )}
        </div>

        <div className={styles.acWork}>
          <div className={styles.acGridWrap}>
            <table className={styles.acGrid}>
              <thead>
                <tr>
                  {ent.cols.map((c) => (
                    <th key={c.key} className={c.right ? styles.acRight : undefined} aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
                      <button type="button" className={styles.acTh} onClick={() => sortBy(c.key)}>
                        {c.label}
                        {sort?.key === c.key && (sort.dir === 1 ? <ChevronUp12Regular /> : <ChevronDown12Regular />)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.map((r) => (
                  <tr key={r.id} className={r.id === selId ? styles.acRowSel : undefined} aria-selected={r.id === selId} tabIndex={0} onClick={() => setSelId(r.id)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setSelId(r.id))}>
                    {ent.cols.map((c) => {
                      const v = val(c, r, data);
                      return (
                        <td key={c.key} className={`${c.right ? styles.acRight : ""} ${c.kind === "mono" ? styles.acMono : ""} ${c.kind === "money" && num(v) < 0 ? styles.acNeg : ""}`}>
                          {c.kind === "status" ? v ? <Pill text={String(v)} /> : null : c.kind === "bar" ? <UseBar pct={num(v)} /> : show(c, v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {view.length === 0 && (
                  <tr>
                    <td colSpan={ent.cols.length} className={styles.acEmpty}>
                      Nothing here yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {sel && (
            <aside className={styles.acDetail} aria-label="Details">
              <div className={styles.acDetailHead}>
                <strong>{sel.id}</strong>
                <Button size="small" appearance="subtle" icon={<Dismiss20Regular />} aria-label="Close details" onClick={() => setSelId(null)} />
              </div>
              <dl className={styles.acDl}>
                {ent.cols
                  .filter((c) => c.key !== "id")
                  .map((c) => {
                    const v = val(c, sel, data);
                    return (
                      <div key={c.key} className={styles.acDlRow}>
                        <dt>{c.label}</dt>
                        <dd>{c.kind === "status" ? v ? <Pill text={String(v)} /> : "—" : c.kind === "bar" ? <UseBar pct={num(v)} /> : show(c, v)}</dd>
                      </div>
                    );
                  })}
              </dl>
              {ent.detail && ent.detail(sel, data)}
            </aside>
          )}
        </div>

        <div className={styles.acFoot}>
          <span>
            {view.length} of {list.length} records
          </span>
          {moneyCol && view.length > 0 && (
            <span>
              Total {moneyCol.label.toLowerCase()}: <strong>{money(moneyTotal)}</strong>
            </span>
          )}
        </div>
      </div>

      {form && (
        <RecordDialog
          key={`${form}${sel?.id ?? ""}`}
          title={form === "edit" ? `Edit ${ent.noun}` : `New ${ent.noun}`}
          submit={form === "edit" ? "Save" : "Create"}
          fields={ent.fields}
          data={data}
          initial={init}
          existing={form === "edit"}
          onClose={() => setForm(null)}
          onSave={save}
        />
      )}
      {prompt && prompt.prompt && sel && (
        <RecordDialog
          key={prompt.label}
          title={prompt.prompt.title}
          submit={prompt.prompt.submit}
          fields={prompt.prompt.fields}
          data={data}
          initial={prompt.prompt.init(sel, data)}
          existing={false}
          onClose={() => setPrompt(null)}
          onSave={async (vals) => {
            const bad = await runAct(prompt, vals);
            if (!bad) setPrompt(null);
            return bad;
          }}
        />
      )}

      <Dialog open={confirmDel} onOpenChange={(_, dd) => !dd.open && setConfirmDel(false)}>
        <DialogSurface className={styles.acDialogSm}>
          <DialogBody>
            <DialogTitle>Delete {ent.noun}?</DialogTitle>
            <DialogContent>{sel ? `${sel.id} will be removed. This can't be undone.` : ""}</DialogContent>
            <DialogActions>
              <DialogTrigger action="close">
                <Button size="small">Cancel</Button>
              </DialogTrigger>
              <Button size="small" className={styles.acDanger} appearance="primary" disabled={busy} onClick={remove}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};


/* ---------- cashflow ---------- */
const tilesOf = (t: { label: string; value: number | null; note?: string | null }[] | undefined): Tile[] =>
  (t ?? []).map((x) => ({ label: x.label, value: x.value === null || x.value === undefined ? "—" : money(x.value), note: x.note ?? undefined }));

const CashflowPage: React.FC<PageProps> = ({ rev }) => {
  const { data: cf, err } = useView(() => view.cashflow(), [rev]);
  if (!cf) return <Waiting err={err} />;
  const dueItems = (l: any[]) => (l ?? []).map((i) => ({ id: String(i.number), who: String(i.party), due: String(i.dueDate), amount: num(i.amount), late: !!i.late }));
  return (
    <>
      <Tiles tiles={tilesOf(cf.tiles)} />
      <div className={styles.acGrid2}>
        <Panel title="Cash in and out" note="Last six months, from bank and cash accounts">
          <DualColumns items={(cf.months ?? []).map((m: any) => ({ label: String(m.label), a: num(m.cashIn), b: num(m.cashOut) }))} aName="Cash in" bName="Cash out" />
        </Panel>
        <Panel title="Cash by account">
          <HBars items={(cf.byAccount ?? []).map((b: any) => ({ label: String(b.label), value: num(b.amount) }))} />
        </Panel>
        <Panel title={`Next ${num(cf.next?.horizonDays)} days`} note="Based on unpaid invoices and bills, including anything overdue">
          <dl className={styles.acSums}>
            <div>
              <dt>Cash today</dt>
              <dd>{money(num(cf.next?.cashToday))}</dd>
            </div>
            <div>
              <dt>Expected receipts</dt>
              <dd>{money(num(cf.next?.expectedReceipts))}</dd>
            </div>
            <div>
              <dt>Expected payments</dt>
              <dd>-{money(num(cf.next?.expectedPayments))}</dd>
            </div>
            <div className={styles.acGrand}>
              <dt>Projected cash</dt>
              <dd className={num(cf.next?.projectedCash) < 0 ? styles.acNeg : undefined}>{money(num(cf.next?.projectedCash))}</dd>
            </div>
          </dl>
        </Panel>
        <Panel title="Coming in" note="Customer invoices due">
          <MiniList items={dueItems(cf.comingIn)} empty="Nothing due." />
        </Panel>
        <Panel title="Going out" note="Vendor bills due" wide>
          <MiniList items={dueItems(cf.goingOut)} empty="Nothing due." />
        </Panel>
      </div>
    </>
  );
};

/* ---------- reconciliation ---------- */
const ReconPage: React.FC<PageProps> = ({ data, reload, say, rev }) => {
  const banks = rows(data, "banks");
  const [bankId, setBankId] = useState("");
  const [stmt, setStmt] = useState("");
  const [ws, setWs] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const asOf = off(0);
  const bank = bankId || banks[0]?.id || "";

  useEffect(() => {
    if (!bank) return;
    let live = true;
    cmd.recon
      .get(bank, asOf, "")
      .then((x) => live && (setWs(x), setErr(null)))
      .catch((e) => live && setErr(errText(e)));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bank, rev]);

  const guard = async (fn: () => Promise<any>) => {
    setBusy(true);
    try {
      const x = await fn();
      if (x && x.lines) setWs(x);
      setErr(null);
      return x;
    } catch (e) {
      say(errText(e), true);
      return null;
    } finally {
      setBusy(false);
    }
  };

  if (!bank) return <div className={styles.acEmpty}>No bank accounts yet.</div>;
  if (!ws) return <Waiting err={err} />;

  const lines: any[] = ws.lines ?? [];
  const cleared = num(ws.clearedBalance);
  const diff = Math.round(((Number(stmt) || 0) - cleared) * 100) / 100;
  const ticked = lines.filter((l) => l.cleared).length;

  const finish = async () => {
    const x = await guard(() => cmd.recon.finish(bank, asOf, stmt));
    if (x) {
      say(`${nameOf(data, "banks", bank)} reconciled`);
      await reload(["banks"]);
    }
  };

  return (
    <>
      <Tiles
        tiles={[
          { label: "Ledger balance", value: money(num(ws.ledgerBalance)), note: "What the books say" },
          { label: "Cleared balance", value: money(cleared), note: `${ticked} of ${lines.length} lines ticked` },
          { label: "Statement balance", value: money(Number(stmt) || 0), note: "What you typed below" },
          { label: "Difference", value: money(diff), note: diff === 0 && stmt ? "Balanced" : "Out of balance" },
        ]}
      />
      <div className={styles.acCard}>
        <div className={styles.acFilters}>
          <Select size="small" className={styles.acSel} value={bank} onChange={(_, dd) => (setWs(null), setBankId(dd.value))} aria-label="Bank account">
            {banks.map((b) => (
              <option key={b.id} value={b.id}>
                {String(b.name)}
              </option>
            ))}
          </Select>
          <Field label="Statement ending balance" orientation="horizontal" className={styles.acInline}>
            <Input size="small" type="number" value={stmt} onChange={(_, dd) => setStmt(dd.value)} />
          </Field>
          <span className={styles.acGrow} />
          <Button size="small" disabled={busy} onClick={() => guard(() => cmd.recon.none(bank, asOf, stmt))}>
            Tick none
          </Button>
          <Button size="small" disabled={busy} onClick={() => guard(() => cmd.recon.all(bank, asOf, stmt))}>
            Tick all
          </Button>
          <Button size="small" className={styles.acPrimary} appearance="primary" disabled={!stmt || diff !== 0 || busy} onClick={finish}>
            Finish reconciliation
          </Button>
        </div>
        <div className={styles.acWork}>
          <div className={styles.acGridWrap}>
            <table className={styles.acGrid}>
              <thead>
                <tr>
                  <th>
                    <span className={styles.acTh}>Cleared</span>
                  </th>
                  <th>
                    <span className={styles.acTh}>Date</span>
                  </th>
                  <th>
                    <span className={styles.acTh}>Reference</span>
                  </th>
                  <th>
                    <span className={styles.acTh}>Description</span>
                  </th>
                  <th className={styles.acRight}>
                    <span className={styles.acTh}>Deposits</span>
                  </th>
                  <th className={styles.acRight}>
                    <span className={styles.acTh}>Withdrawals</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.lineId} className={l.cleared ? styles.acRowSel : undefined}>
                    <td>
                      <Checkbox checked={!!l.cleared} disabled={busy || !!l.locked} onChange={() => guard(() => cmd.recon.toggle(bank, l.lineId, !l.cleared, asOf, stmt))} aria-label={`Clear ${l.reference}`} />
                    </td>
                    <td>{fdate(String(l.date))}</td>
                    <td className={styles.acMono}>{l.reference}</td>
                    <td>{l.description}</td>
                    <td className={styles.acRight}>{num(l.debit) ? money(num(l.debit)) : ""}</td>
                    <td className={styles.acRight}>{num(l.credit) ? money(num(l.credit)) : ""}</td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr>
                    <td colSpan={6} className={styles.acEmpty}>
                      No transactions on this account.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

/* ---------- reports (the server does the arithmetic) ---------- */
const PERIODS: { v: string; l: string }[] = [
  { v: "month", l: "This month" },
  { v: "last", l: "Last month" },
  { v: "quarter", l: "This quarter" },
  { v: "year", l: "This year" },
  { v: "12m", l: "Last 12 months" },
];

const PeriodSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <Select size="small" className={styles.acSel} value={value} onChange={(_, dd) => onChange(dd.value)} aria-label="Period">
    {PERIODS.map((p) => (
      <option key={p.v} value={p.v}>
        {p.l}
      </option>
    ))}
  </Select>
);

const RepRow: React.FC<{ label: string; cur: number; prev?: number; kind?: "sec" | "tot" | "grand" | "line"; indent?: boolean; compare?: boolean; change?: string }> = ({ label, cur, prev, kind = "line", indent, compare, change }) => (
  <tr className={kind === "sec" ? styles.acRepSec : kind === "tot" ? styles.acRepTot : kind === "grand" ? styles.acRepGrand : undefined}>
    <td className={indent ? styles.acIndent : undefined}>{label}</td>
    {kind === "sec" ? (
      <>
        <td />
        {compare && (
          <>
            <td />
            <td />
          </>
        )}
      </>
    ) : (
      <>
        <td className={`${styles.acRight} ${cur < 0 ? styles.acNeg : ""}`}>{money(cur)}</td>
        {compare && (
          <>
            <td className={`${styles.acRight} ${styles.acMuted}`}>{money(prev ?? 0)}</td>
            <td className={`${styles.acRight} ${styles.acMuted}`}>{change ?? pctChange(cur, prev ?? 0)}</td>
          </>
        )}
      </>
    )}
  </tr>
);

const ProfitLossPage: React.FC<PageProps> = ({ rev }) => {
  const [kind, setKind] = useState("year");
  const { data: pl, err } = useView(() => view.profitLoss(kind), [kind, rev]);
  const block = (sec: any, totalLabel: string) => (
    <>
      <RepRow label={String(sec?.name ?? totalLabel)} cur={0} kind="sec" compare />
      {(sec?.rows ?? []).map((x: any) => (
        <RepRow key={x.accountId} label={String(x.name)} cur={num(x.current)} prev={num(x.previous)} change={String(x.change ?? "")} indent compare />
      ))}
      <RepRow label={totalLabel} cur={num(sec?.current)} prev={num(sec?.previous)} kind="tot" compare />
    </>
  );
  return (
    <>
      <div className={styles.acFiltersBare}>
        <PeriodSelect value={kind} onChange={setKind} />
        {pl && (
          <span className={styles.acHint}>
            {fdate(pl.period.from)} to {fdate(pl.period.to)}, compared with {fdate(pl.period.previousFrom)} to {fdate(pl.period.previousTo)}
          </span>
        )}
      </div>
      {!pl ? (
        <Waiting err={err} />
      ) : (
        <>
          <Tiles tiles={tilesOf(pl.tiles)} />
          <div className={styles.acCard}>
            <div className={styles.acGridWrap}>
              <table className={styles.acGrid}>
                <thead>
                  <tr>
                    <th>
                      <span className={styles.acTh}>Account</span>
                    </th>
                    <th className={styles.acRight}>
                      <span className={styles.acTh}>This period</span>
                    </th>
                    <th className={styles.acRight}>
                      <span className={styles.acTh}>Previous</span>
                    </th>
                    <th className={styles.acRight}>
                      <span className={styles.acTh}>Change</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {block(pl.revenue, "Total revenue")}
                  {block(pl.costOfSales, "Total cost of sales")}
                  <RepRow label="Gross profit" cur={num(pl.grossProfit)} prev={num(pl.grossProfitPrevious)} kind="grand" compare />
                  {block(pl.operatingExpenses, "Total operating expenses")}
                  <RepRow label="Net profit" cur={num(pl.netProfit)} prev={num(pl.netProfitPrevious)} kind="grand" compare />
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const BalanceSheetPage: React.FC<PageProps> = ({ rev }) => {
  const [asAt, setAsAt] = useState(off(0));
  const { data: bs, err } = useView(() => view.balanceSheet(asAt), [asAt, rev]);
  const lines = (sec: any) =>
    (sec?.lines ?? []).map((x: any) => <RepRow key={x.accountId} label={`${x.code} · ${String(x.name)}`} cur={num(x.balance)} indent />);
  return (
    <>
      <div className={styles.acFiltersBare}>
        <Field label="As at" orientation="horizontal" className={styles.acInline}>
          <Input size="small" type="date" value={asAt} onChange={(_, dd) => dd.value && setAsAt(dd.value)} />
        </Field>
        {bs && <Pill text={bs.balanced ? "Balanced" : "Out of balance"} />}
      </div>
      {!bs ? (
        <Waiting err={err} />
      ) : (
        <>
          <Tiles tiles={tilesOf(bs.tiles)} />
          <div className={styles.acCard}>
            <div className={styles.acGridWrap}>
              <table className={styles.acGrid}>
                <thead>
                  <tr>
                    <th>
                      <span className={styles.acTh}>Account</span>
                    </th>
                    <th className={styles.acRight}>
                      <span className={styles.acTh}>Balance</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <RepRow label="Assets" cur={0} kind="sec" />
                  {lines(bs.assets)}
                  <RepRow label="Total assets" cur={num(bs.assets?.total)} kind="grand" />
                  <RepRow label="Liabilities" cur={0} kind="sec" />
                  {lines(bs.liabilities)}
                  <RepRow label="Total liabilities" cur={num(bs.liabilities?.total)} kind="tot" />
                  <RepRow label="Equity" cur={0} kind="sec" />
                  {lines(bs.equity)}
                  <RepRow label="Current period earnings" cur={num(bs.currentEarnings)} indent />
                  <RepRow label="Total equity" cur={num(bs.totalEquity)} kind="tot" />
                  <RepRow label="Total liabilities and equity" cur={num(bs.totalLiabilitiesAndEquity)} kind="grand" />
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const TaxPage: React.FC<PageProps> = ({ rev }) => {
  const [kind, setKind] = useState("quarter");
  const { data: tx, err } = useView(() => view.tax(kind), [kind, rev]);
  const head = (t: string, right = false) => (
    <th className={right ? styles.acRight : undefined}>
      <span className={styles.acTh}>{t}</span>
    </th>
  );
  return (
    <>
      <div className={styles.acFiltersBare}>
        <PeriodSelect value={kind} onChange={setKind} />
        {tx && (
          <span className={styles.acHint}>
            {fdate(tx.period.from)} to {fdate(tx.period.to)}
          </span>
        )}
      </div>
      {!tx ? (
        <Waiting err={err} />
      ) : (
        <>
          <Tiles tiles={tilesOf(tx.tiles)} />
          <div className={styles.acGrid2}>
            <Panel title="By month" wide>
              <table className={styles.acGrid}>
                <thead>
                  <tr>
                    {head("Month")}
                    {head("Taxable sales", true)}
                    {head("VAT collected", true)}
                    {head("Taxable purchases", true)}
                    {head("VAT paid", true)}
                    {head("Net VAT", true)}
                  </tr>
                </thead>
                <tbody>
                  {(tx.byMonth ?? []).map((m: any) => (
                    <tr key={String(m.start)}>
                      <td>{String(m.month)}</td>
                      <td className={styles.acRight}>{money(num(m.taxableSales))}</td>
                      <td className={styles.acRight}>{money(num(m.vatCollected))}</td>
                      <td className={styles.acRight}>{money(num(m.taxablePurchases))}</td>
                      <td className={styles.acRight}>{money(num(m.vatPaid))}</td>
                      <td className={styles.acRight}>{money(num(m.netVat))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
            <Panel title="Taxable transactions" wide>
              <table className={styles.acGrid}>
                <thead>
                  <tr>
                    {head("Date")}
                    {head("Document")}
                    {head("Type")}
                    {head("Party")}
                    {head("Amount", true)}
                    {head("VAT", true)}
                  </tr>
                </thead>
                <tbody>
                  {(tx.transactions ?? []).map((r: any, i: number) => (
                    <tr key={`${r.document}-${i}`}>
                      <td>{fdate(String(r.date))}</td>
                      <td className={styles.acMono}>{String(r.document)}</td>
                      <td>{String(r.type)}</td>
                      <td>{String(r.party)}</td>
                      <td className={styles.acRight}>{money(num(r.amount))}</td>
                      <td className={styles.acRight}>{money(num(r.vat))}</td>
                    </tr>
                  ))}
                  {(tx.transactions ?? []).length === 0 && (
                    <tr>
                      <td colSpan={6} className={styles.acEmpty}>
                        No taxable transactions in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Panel>
          </div>
        </>
      )}
    </>
  );
};

/* ---------- extra modules: any API list, shown as a live read-only grid ---------- */
const ModulePage: React.FC<PageProps & { def: ModuleDef }> = ({ def, ...p }) => {
  const { data: tbl, err } = useView<ModuleTable>(() => loadModule(def), [def.id, p.rev]);
  const ent = useMemo<Entity | null>(
    () =>
      tbl
        ? {
            id: def.id,
            src: def.id,
            title: def.title,
            subtitle: def.subtitle,
            noun: "record",
            page: "grid",
            readOnly: true,
            cols: tbl.columns.map((c) => ({ key: c.key, label: c.label, kind: c.kind === "text" ? undefined : c.kind, right: c.kind === "money" || c.kind === "num" })),
            fields: [],
            acts: [],
          }
        : null,
    [tbl, def],
  );
  if (!ent || !tbl) return <Waiting err={err} />;
  return <EntityPage key={def.id} ent={ent} list={tbl.rows} {...p} />;
};

/* ---------- navigation ---------- */
interface NavItem {
  id: string;
  title: string;
  icon: React.ReactElement;
}
const NAV_CORE: { group: string; items: NavItem[] }[] = [
  {
    group: "Finance",
    items: [
      { id: "Dashboard", title: "Dashboard", icon: <AppTitleRegular /> },
      { id: "Cashflow", title: "Cashflow", icon: <ChartMultipleRegular /> },
      { id: "Budget", title: "Budget", icon: <DocumentTableRegular /> },
    ],
  },
  {
    group: "Transactions",
    items: [
      { id: "Invoices", title: "Invoices", icon: <ReceiptRegular /> },
      { id: "Payments", title: "Payments", icon: <MoneyRegular /> },
      { id: "Payables", title: "Payables", icon: <WalletCreditCardRegular /> },
      { id: "Expenses", title: "Expenses", icon: <ReceiptMoneyRegular /> },
      { id: "Journal", title: "Journal entries", icon: <BookRegular /> },
    ],
  },
  {
    group: "Contacts",
    items: [
      { id: "Customers", title: "Customers", icon: <PeopleRegular /> },
      { id: "Vendors", title: "Vendors", icon: <BuildingRegular /> },
    ],
  },
  {
    group: "Banking",
    items: [
      { id: "Banks", title: "Bank accounts", icon: <BuildingBankRegular /> },
      { id: "Reconciliation", title: "Reconciliation", icon: <ArrowSyncCircleRegular /> },
    ],
  },
  {
    group: "Ledger",
    items: [
      { id: "Accounts", title: "Accounts", icon: <TextBulletListLtrRegular /> },
      { id: "Ledger", title: "General ledger", icon: <BookOpenRegular /> },
      { id: "Trial", title: "Trial balance", icon: <ScalesRegular /> },
      { id: "Assets", title: "Fixed assets", icon: <BoxRegular /> },
    ],
  },
  {
    group: "Reports",
    items: [
      { id: "P&L", title: "Profit & Loss", icon: <NoteEditRegular /> },
      { id: "Balance Sheet", title: "Balance Sheet", icon: <CalendarMonthRegular /> },
      { id: "Tax", title: "Tax Reports", icon: <TopSpeedRegular /> },
    ],
  },
  {
    group: "Setup",
    items: [
      { id: "TaxRates", title: "Tax rates", icon: <DocumentBulletListRegular /> },
      { id: "Audit", title: "Audit log", icon: <HistoryRegular /> },
    ],
  },
];
const NAV: { group: string; items: NavItem[] }[] = [
  ...NAV_CORE,
  ...Array.from(new Set(MODULES.map((m) => m.group))).map((g) => ({
    group: `More · ${g}`,
    items: MODULES.filter((m) => m.group === g).map((m) => ({ id: m.id, title: m.title, icon: <DocumentTableRegular /> })),
  })),
];

const AccountPage: React.FC<PageProps & { tab: string }> = ({ tab, ...p }) => {
  const def = MODULES.find((m) => m.id === tab);
  const ent = ENTS.find((e) => e.id === tab);
  const title = def?.title ?? ent?.title;
  if (!title) return null;
  return (
    <div className={styles.acPg}>
      <header className={styles.acHead}>
        <h2 className={styles.acTitle}>{title}</h2>
        <p className={styles.acSub}>{def?.subtitle ?? ent?.subtitle}</p>
      </header>
      {def && <ModulePage key={def.id} def={def} {...p} />}
      {ent && ent.page === "grid" && <EntityPage key={ent.id} ent={ent} {...p} />}
      {ent && ent.page === "cash" && <CashflowPage {...p} />}
      {ent && ent.page === "recon" && <ReconPage {...p} />}
      {ent && ent.page === "pl" && <ProfitLossPage {...p} />}
      {ent && ent.page === "bs" && <BalanceSheetPage {...p} />}
      {ent && ent.page === "tax" && <TaxPage {...p} />}
    </div>
  );
};

/* =====================================================================================================
   Dashboard (home): one call to /api/dashboard/overview, turned into the shapes the cards draw.
   ===================================================================================================== */
const PALETTE = ["#0f6cbd", "#2b88d8", "#6cb8f6", "#8cbcd2", "#b4d6fa", "#d0d7de"];
const conic = (vals: number[]): string => {
  const total = vals.reduce((a, b) => a + Math.max(b, 0), 0);
  if (total <= 0) return "conic-gradient(#e5e7eb 0 100%)";
  let at = 0;
  const parts = vals.map((v, i) => {
    const from = at;
    at += (Math.max(v, 0) / total) * 100;
    return `${PALETTE[i % PALETTE.length]} ${from}% ${at}%`;
  });
  return `conic-gradient(${parts.join(", ")})`;
};
const scale = (vals: number[]): number[] => {
  const mx = Math.max(...vals.map((v) => Math.abs(v)), 0);
  return vals.map((v) => (mx > 0 ? Math.max(0, Math.round((v / mx) * 100)) : 0));
};
const axis = (mx: number, steps = 4): string[] => Array.from({ length: steps + 1 }, (_, i) => compactMoney((mx / steps) * (steps - i)).replace(/[^\d.KMBT]/g, "") || "0");
const linePath = (vals: number[], mx: number): string =>
  vals.length === 0
    ? ""
    : vals
        .map((v, i) => `${i === 0 ? "M" : "L"}${vals.length === 1 ? 0 : Math.round((i / (vals.length - 1)) * 700)} ${Math.round(149 - (mx > 0 ? Math.max(v, 0) / mx : 0) * 135)}`)
        .join(" ");
const pctText = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${Math.round(v)}%`);

const Overview: React.FC<{ ov: any; period: ReactNode; cal: ReactNode }> = ({ ov, period, cal }) => {
  const pay: any[] = ov.payablesSummary ?? [];
  const cfs: any[] = ov.cashflowSummary ?? [];
  const exps: any[] = ov.expenseSummary ?? [];
  const revs: any[] = ov.revenueSources ?? [];
  const avb: any[] = ov.actualVsBudget ?? [];
  const high: any[] = ov.highestExpenses ?? [];
  const alloc = ov.capitalAllocation ?? {};
  const cfMax = Math.max(...cfs.flatMap((c) => [Math.abs(num(c.actual)), Math.abs(num(c.budget))]), 1);
  const payMax = Math.max(...pay.map((p) => num(p.billed)), 1);
  const revMax = Math.max(...revs.flatMap((r) => [num(r.actual), num(r.budget)]), 1);
  const avbMax = Math.max(...avb.flatMap((r) => [num(r.actual), num(r.budget)]), 1);
  void period;
  return (
    <main className={styles.dashboard}>
      <div className={styles["top-band"]} />
      <div className={styles["dashboard-grid"]}>
        <header className={styles.intro}>
          <p className={styles.overline}>Enterprise finance suite</p>
          <h2>FINANCE OPERATIONS DASHBOARD</h2>
          <p>Bookkeeping, accounting &amp; financial performance</p>

          <div className={styles["top-metrics"]}>
            <Card className={styles["mini-card"]}>
              <MiniIcon type="expense" />
              <div>
                <b>Expenses</b>
                <small>Operating spend</small>
                <strong>{compactMoney(num(ov.expenses))}</strong>
              </div>
            </Card>
            <Card className={styles["mini-card"]}>
              <MiniIcon type="income" />
              <div>
                <b>Revenue</b>
                <small>Recognized income</small>
                <strong>{compactMoney(num(ov.revenue))}</strong>
              </div>
            </Card>
            <Card className={styles["mini-card"]}>
              <MiniIcon type="bill" />
              <div>
                <b>Payables</b>
                <small>Open obligations</small>
                <strong>{compactMoney(num(ov.payables))}</strong>
              </div>
            </Card>
          </div>
        </header>

        <Card className={styles.allocation}>
          <div className={styles["center-heading"]}>
            <h2>Capital Allocation</h2>
            <p>Actual allocation of operating income</p>
          </div>
          <div className={styles["allocation-chart"]}>
            <div className={`${styles.donut} ${styles["alloc-donut"]}`} style={{ background: conic([num(alloc.reservesAmount), num(alloc.expensesAmount), num(alloc.payablesAmount)]) }} />
            <span className={`${styles.callout} ${styles.savings}`}>
              Reserves<br />
              <b>{pctText(num(alloc.reservesPercent))}</b>
            </span>
            <span className={`${styles.callout} ${styles.expenses}`}>
              Expenses<br />
              <b>{pctText(num(alloc.expensesPercent))}</b>
            </span>
            <span className={`${styles.callout} ${styles.bills}`}>
              Payables<br />
              <b>{pctText(num(alloc.payablesPercent))}</b>
            </span>
          </div>
        </Card>

        <aside className={styles["right-top"]}>
          {cal}
          <Card className={styles.balance}>
            <h3>Available Cash</h3>
            <p>Current liquidity</p>
            <strong>{compactMoney(num(ov.availableCash))}</strong>
          </Card>
        </aside>

        <Card className={`${styles["bill-summary"]} ${styles["chart-card"]}`}>
          <div className={styles["card-title"]}>
            <i />
            <div>
              <h2>Payables Summary</h2>
              <p>Actual bill payments</p>
            </div>
          </div>
          <div className={styles["chart-content"]}>
            <div className={styles["y-axis"]}>
              {axis(payMax).map((l, i) => (
                <span key={i}>{l}</span>
              ))}
            </div>
            <Bars values={scale(pay.map((p) => num(p.billed)))} max={100} />
          </div>
          <div className={styles["x-labels"]}>
            {pay.map((p) => (
              <span key={String(p.label)}>{String(p.label)}</span>
            ))}
          </div>
        </Card>

        <Card className={`${styles.cashflow} ${styles["dark-card"]}`}>
          <div className={`${styles["card-title"]} ${styles.light}`}>
            <i />
            <div>
              <h2>Cashflow Summary</h2>
              <p>Actual vs. forecast</p>
            </div>
          </div>
          <div className={styles["cash-legend"]}>
            <span>
              <i />
              Actual
            </span>
            <span>
              <i />
              Budget
            </span>
          </div>
          <div className={styles["horizontal-chart"]}>
            {cfs.map((row, i) => (
              <div className={styles["h-row"]} key={i}>
                <label>{String(row.label)}</label>
                <div>
                  <span style={{ width: `${Math.max(0, (num(row.actual) / cfMax) * 100)}%` }} />
                  <b style={{ width: `${row.budget === null || row.budget === undefined ? 0 : Math.max(0, (num(row.budget) / cfMax) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className={styles["h-axis"]}>
            {axis(cfMax)
              .reverse()
              .map((l, i) => (
                <span key={i}>{l}</span>
              ))}
          </div>
        </Card>

        <Card className={styles["expense-summary"]}>
          <div className={styles["card-title"]}>
            <i />
            <div>
              <h2>Expense Summary</h2>
              <p>Actual expenses by function</p>
            </div>
          </div>
          <div className={styles["expense-content"]}>
            <ul>
              {exps.map((e) => (
                <li key={String(e.label)}>{String(e.label)}</li>
              ))}
            </ul>
            <div className={`${styles.donut} ${styles["expense-donut"]}`} style={{ background: conic(exps.map((e) => num(e.amount))) }} />
          </div>
        </Card>

        <Card className={`${styles["budget-card"]} ${styles["side-card"]}`}>
          <div className={styles["card-title"]}>
            <i />
            <div>
              <h2>Budget vs. Actual</h2>
              <p>Performance to plan</p>
            </div>
          </div>
          <h4>Savings rate</h4>
          <div className={styles.kv}>
            <span>Budget</span>
            <b>{pctText(ov.savingsRate?.budget)}</b>
          </div>
          <div className={styles.kv}>
            <span>Actual</span>
            <b>{pctText(ov.savingsRate?.actual)}</b>
          </div>
          <h4>Payables</h4>
          <div className={styles.kv}>
            <span>Paid</span>
            <b>{String(num(ov.payablesStatus?.paid))}</b>
          </div>
          <div className={styles.kv}>
            <span>Outstanding</span>
            <b>{String(num(ov.payablesStatus?.outstanding))}</b>
          </div>
        </Card>

        <Card className={`${styles["income-source"]} ${styles["chart-card"]}`}>
          <div className={styles["card-title"]}>
            <i />
            <div>
              <h2>Revenue Sources</h2>
              <p>Actual vs. budget</p>
            </div>
          </div>
          <div className={styles["chart-content"]}>
            <div className={styles["y-axis"]}>
              {axis(revMax, 3).map((l, i) => (
                <span key={i}>{l}</span>
              ))}
            </div>
            <Bars values={scale(revs.map((r) => num(r.actual)))} max={100} />
          </div>
          <div className={`${styles["x-labels"]} ${styles.three}`}>
            {revs.map((r) => (
              <span key={String(r.label)}>{String(r.label)}</span>
            ))}
          </div>
        </Card>

        <Card className={styles["actual-budget"]}>
          <div className={styles["card-title"]}>
            <i />
            <div>
              <h2>Actual vs. Budget</h2>
              <p>Operating expense trend</p>
            </div>
          </div>
          <div className={styles["line-area"]}>
            <div className={styles["line-y"]}>
              {axis(avbMax).map((l, i) => (
                <span key={i}>{l}</span>
              ))}
            </div>
            <svg viewBox="0 0 700 150" preserveAspectRatio="none">
              <g>
                <path d="M0 10H700M0 45H700M0 80H700M0 115H700M0 149H700" />
              </g>
              <path className={styles["budget-line"]} d={linePath(avb.map((r) => num(r.budget)), avbMax)} />
              <path className={styles["actual-line"]} d={linePath(avb.map((r) => num(r.actual)), avbMax)} />
            </svg>
          </div>
          <div className={styles["line-labels"]}>
            {avb.map((r) => (
              <span key={String(r.label)}>{String(r.label)}</span>
            ))}
          </div>
        </Card>

        <Card className={`${styles.highest} ${styles["side-card"]}`}>
          <div className={styles["card-title"]}>
            <i />
            <div>
              <h2>Highest Expenses</h2>
            </div>
          </div>
          {high.map((h) => (
            <div key={String(h.label)} className={`${styles.kv} ${styles.large}`}>
              <span>{String(h.label)}</span>
              <b>{compactMoney(num(h.amount))}</b>
            </div>
          ))}
        </Card>
      </div>
    </main>
  );
};

const Accounting: React.FC = () => {
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [books, setBooks] = useState<Data>({});
  const [ready, setReady] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [rev, setRev] = useState(0);
  const [ov, setOv] = useState<any>(null);
  const [ovErr, setOvErr] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const live = useRef<Data>({});
  live.current = books;
  CTX.rev = rev;

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);
  const say = (msg: string, err = false) => setToast({ msg, err });

  const reload = useCallback(async (keys?: string[]) => {
    const { data, errors } = await load(keys && keys.length ? keys : ALL_KEYS, live.current);
    live.current = data;
    setBooks(data);
    setProblems(errors);
    setRev((x) => x + 1);
  }, []);

  useEffect(() => {
    reload().finally(() => setReady(true));
  }, [reload]);

  const year = selectedDate.getFullYear();
  const monthIndex = selectedDate.getMonth();
  const selectedMonthLabel = `${monthNames[monthIndex]} ${year}`;

  useEffect(() => {
    let alive = true;
    view
      .overview(year, monthIndex + 1)
      .then((x) => {
        if (!alive) return;
        setCurrency(x?.currency);
        setOv(x);
        setOvErr(null);
      })
      .catch((e) => alive && setOvErr(errText(e)));
    return () => {
      alive = false;
    };
  }, [year, monthIndex, rev]);

  const onSelectDate = (date: Date) => {
    setSelectedDate(date);
    setCalendarOpen(false);
  };

  const handleClick = (tab: string) => setActiveTab(tab);

  const calendar = (
    <div className={styles.month}>
      <span>Reporting period</span>
      <Popover open={calendarOpen} onOpenChange={(_, d) => setCalendarOpen(d.open)}>
        <PopoverTrigger disableButtonEnhancement>
          <Button appearance="transparent" className={styles["month-trigger"]} icon={<ChevronDown16Regular />} iconPosition="after">
            {selectedMonthLabel}
          </Button>
        </PopoverTrigger>
        <PopoverSurface className={styles["calendar-popover"]}>
          <Calendar showMonthPickerAsOverlay highlightSelectedMonth showGoToToday onSelectDate={onSelectDate} value={selectedDate} />
        </PopoverSurface>
      </Popover>
    </div>
  );

  return (
    <div className={styles.gU1Tf}>
      <div className={styles.jUsfu}>
        <div className={styles.QIl4y}>
          <div className={styles.O0VOp1}>
            <div className={styles.VvU3MOvL}>

              {/* ===== TOP ACTION BAR (same style as CRM) ===== */}
              <div className={styles.clieTWU2V}>
                <div className={styles.clieTWU2Vc}>
                  <Button size="small" className={`${styles.vgnei} ${styles.cgTactive}`}>
                    <span className={`${styles.ci_menu_toggle} ${styles.border0} ${styles.hasImage}`}>
                      <img src={AccountingLogo} alt="Accounting" height={22} width={22} />
                    </span>
                    <span className={`${styles.ci_menu_brand} ${styles.dflex} ${styles.pe0}`}>
                      Accounting
                    </span>
                  </Button>

                  <Button shape="circular" size="small" className={styles.Tab6re}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button className={styles.Tab6re} icon={<Timer16Regular />}>
                          Recent <ChevronDown16Regular />
                        </Button>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem onClick={() => handleClick("Invoices")}>Invoices</MenuItem>
                          <MenuItem onClick={() => handleClick("Payments")}>Payments</MenuItem>
                          <MenuItem onClick={() => handleClick("P&L")}>Reports</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </Button>

                  <Button size="small" shape="circular" className={styles.JKLow}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button className={styles.JKLow} icon={<Pin16Regular />}>
                          Pinned <ChevronDown16Regular />
                        </Button>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem onClick={() => handleClick("Cashflow")}>Cashflow</MenuItem>
                          <MenuItem onClick={() => handleClick("Payables")}>Payables</MenuItem>
                          <MenuItem onClick={() => handleClick("Budget")}>Budget</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </Button>
                </div>

                <div className={styles.clieTWU2Vc}>
                  <Button shape="circular" size="small" className={styles.narTIl}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <MenuButton icon={<Settings16Regular />} className={styles.narTIl} menuIcon={<ChevronDown16Regular />}>
                          Actions
                        </MenuButton>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem icon={<ArrowSyncCircleRegular />} onClick={() => reload()}>
                            Refresh data
                          </MenuItem>
                          <MenuItem icon={<ArrowDownloadRegular />}>Import Records</MenuItem>
                          <MenuItem icon={<DocumentArrowDownRegular />}>Spreadsheets</MenuItem>
                          <MenuItem icon={<DocumentFlowchartRegular />}>Insert View</MenuItem>
                          <MenuItem icon={<LinkRegular />}>Insert Link</MenuItem>
                          <MenuItem icon={<DocumentDataLinkRegular />}>Link Menu</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </Button>
                </div>
              </div>

              {/* ===== SIDEBAR + CONTENT ===== */}
              <div className={styles.BHt3}>
                {/* LEFT SIDEBAR */}
                <div className={styles.UIy67b}>
                  <div className={styles.r6YRf3}>
                    <List selectionMode="single" navigationMode="composite" className={`${styles.ERFwq} ${styles.customScrollBar}`}>
                      {NAV.map((g) => (
                        <React.Fragment key={g.group}>
                          <h2 className={styles.YCFowe}>{g.group}</h2>
                          <ListItem className={styles.YCFSra} checkmark={null}>
                            <TabList vertical selectedValue={activeTab}>
                              {g.items.map((it) => (
                                <Tab key={it.id} value={it.id} onClick={() => handleClick(it.id)} className={`${styles.Yuisvt} ${activeTab === it.id ? styles.cgTactive : ""}`} icon={it.icon}>
                                  {it.title}
                                </Tab>
                              ))}
                            </TabList>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  </div>
                </div>

                {/* MAIN CONTENT AREA */}
                <div className={styles.YtRw}>
                  {problems.length > 0 && (
                    <div className={styles.acErr} role="alert">
                      {problems.every((p) => p.includes("Can't reach"))
                        ? "Can't reach the accounting service. Check your connection and try again."
                        : `Some data could not be loaded: ${problems.join(" · ")}`}{" "}
                      <Button size="small" appearance="subtle" onClick={() => reload()}>
                        Try again
                      </Button>
                    </div>
                  )}
                  {!ready && <Skeleton kind={activeTab === "Dashboard" ? "dash" : "page"} />}
                  {ready && activeTab === "Dashboard" && (ov ? <Overview ov={ov} period={null} cal={calendar} /> : ovErr ? <Waiting err={ovErr} /> : <Skeleton kind="dash" />)}
                  {ready && activeTab !== "Dashboard" && <AccountPage tab={activeTab} data={books} reload={reload} say={say} rev={rev} />}
                </div>
              </div>
              {toast && (
                <div className={`${styles.acToast} ${toast.err ? styles.acToastErr : ""}`} role="status" aria-live="polite">
                  {toast.msg}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accounting;